import type {InternalAxiosRequestConfig, AxiosInstance, AxiosResponse, AxiosError} from 'axios';
import {AxiosHeaders} from 'axios';
import {camelizeKeys, decamelizeKeys, HumpsProcessor} from 'humps';

import type {FirebaseTokenProvider} from 'services/Firebase/FirebaseTokenProvider';
import {TasksObserver} from 'services/TasksObserver/TasksObserver';
import store from 'store';
import {getActiveProjectFromLocalStorage} from 'store/profile/utils';

import {accessControl} from './accesscontrol';

export const camelCaseConverter: HumpsProcessor = (key, convert, options) => {
  if (key.includes('-')) return key;
  return convert(key, options);
};

const createAuthInterceptor = (tokenProvider: FirebaseTokenProvider) => {
  return async (config: InternalAxiosRequestConfig) => {
    if (!config.headers) {
      config.headers = new AxiosHeaders();
    }

    const token = await tokenProvider.getUserIdToken();
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    config.headers['X-Journey-Platform'] = window.location.pathname.includes('signup/alt-a') ? 'c4_konvert' : 'c4';

    return config;
  };
};

const createRequestTransformInterceptor = () => {
  return (config: InternalAxiosRequestConfig) => {
    if (
      config.headers['Content-Type'] &&
      typeof config.headers['Content-Type'] === 'string' &&
      config.headers['Content-Type'].includes('multipart/form-data')
    ) {
      return config;
    }

    config.params = config.params && decamelizeKeys(config.params, camelCaseConverter);
    config.data = config.data && decamelizeKeys(config.data, camelCaseConverter);
    return config;
  };
};

const createResponseTransformInterceptor = () => {
  return (response: AxiosResponse) => {
    if (response.config.disableCamelCaseConverter) {
      return response;
    }

    if (response.data && response.headers['content-type']?.includes('application/json')) {
      if (
        !response.config.ignoreAffectedTaskIds &&
        Array.isArray(response.data?.affected_task_ids) &&
        response.data.affected_task_ids.length > 0
      ) {
        const state = store.getState();
        TasksObserver.getInstance().load({
          ids: response.data.affected_task_ids,
          projectId: getActiveProjectFromLocalStorage(state.profile.worker?.id ?? '', state.profile.activeCompanyId),
        });
      }

      let savedSamples: any[] = [];
      if (response.config.url?.includes('prep/status') && response.data?.result?.samples) {
        savedSamples = [...response.data.result.samples];
      }

      response.data = camelizeKeys(response.data, camelCaseConverter);

      if (savedSamples.length) {
        response.data.result.samples = savedSamples;
      }
    }
    return response;
  };
};

const createErrorInterceptor = () => {
  return (error: AxiosError) => {
    if (error.response?.status === 403) {
      accessControl.handleAccessDenied();
    }
    return Promise.reject(error);
  };
};

export const setupInterceptors = (instance: AxiosInstance, tokenProvider: FirebaseTokenProvider): void => {
  // Set the instance in AccessControl
  accessControl.setAxiosInstance(instance);

  // Request interceptors
  instance.interceptors.request.use(createAuthInterceptor(tokenProvider), (error: AxiosError) => Promise.reject(error));

  instance.interceptors.request.use(createRequestTransformInterceptor(), (error: AxiosError) => Promise.reject(error));

  // Response interceptors
  instance.interceptors.response.use(createResponseTransformInterceptor(), createErrorInterceptor());
};
